import { Injectable } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable, Subject, forkJoin } from "rxjs";
import { filter, mergeMap, takeUntil } from "rxjs/operators";
import { LanguageDTO } from "src/app/data/schema/Subscriptions/LanguageDTO";
import { SubscriptionDTO } from "src/app/data/schema/Subscriptions/SubscriptionDTO";
import { ApiSubscriptionsService } from "src/app/data/service/subscriptions/api-subscriptions.service";
import { environment } from "src/environments/environment";
import { ThemeConfig } from "./theme/theme-config";
import { ThemingService } from "./theme/theming.service";

@Injectable({
    providedIn: "root"
})
export class SubscriptionService {
    private LOCAL_STORAGE_CURRENT_SUB = "current_subscription";

    private subscriptions$: BehaviorSubject<SubscriptionDTO[]>;
    public subscriptionsAsync = (): Observable<SubscriptionDTO[]> => this.subscriptions$.asObservable();

    public currentSubscription$: BehaviorSubject<SubscriptionDTO>;
    public currentSubscriptionAsync = (): Observable<SubscriptionDTO> => this.currentSubscription$.asObservable();

    get currentSubscription(): SubscriptionDTO {
        return this.currentSubscription$.getValue();
    }

    // Private
    private _unsubscribeAll: Subject<any>;

    constructor(
        private _apiSubscriptionsService: ApiSubscriptionsService,
        private _themingService: ThemingService,
        private titleService: Title,
        private translateService: TranslateService
    ) {
        this.subscriptions$ = new BehaviorSubject<SubscriptionDTO[]>([]);

        const cachedSubscription: SubscriptionDTO =
            JSON.parse(localStorage.getItem(this.LOCAL_STORAGE_CURRENT_SUB)) || (null as SubscriptionDTO);
        this.currentSubscription$ = new BehaviorSubject<SubscriptionDTO>(cachedSubscription);

        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    public initialize(): Promise<any> {
        this.subscribeToCurrentSubscriptionChanges();

        this._themingService.setCached(location.hostname);

        return this._apiSubscriptionsService
            .GetSubscriptionByHost(
                location.hostname != "localhost" ? location.hostname : environment.default_published_url
            )
            .pipe(
                takeUntil(this._unsubscribeAll),
                mergeMap((response: SubscriptionDTO) => {
                    this.subscriptions$.next([response]);
                    this.currentSubscription$.next(response);

                    return this._themingService.initialize(this.generateTheme(this.currentSubscription$.getValue()));
                })
            )
            .toPromise();
    }

    public listSubscriptionsByHost(hosts: string[]): void {
        const subscriptions: SubscriptionDTO[] = [];

        const requisitions = [];
        hosts.forEach((host) => {
            requisitions.push(this._apiSubscriptionsService.GetSubscriptionByHost(host));
        });

        forkJoin(requisitions).subscribe((results: SubscriptionDTO[]) => {
            subscriptions.push(...results);

            this.subscriptions$.next(subscriptions);
            const defaultId = this.currentSubscription$.getValue()?.id ?? environment.default_subscription_id;

            const currentSubOnArray = subscriptions.find((x) => x.id == defaultId);

            this.currentSubscription$.next(currentSubOnArray || subscriptions[0]);
        });
    }

    private subscribeToCurrentSubscriptionChanges(): void {
        this.currentSubscription$
            .pipe(takeUntil(this._unsubscribeAll), filter<SubscriptionDTO>(Boolean))
            .subscribe((sub: SubscriptionDTO) => {
                this.titleService.setTitle(sub.name);
                const newTheme: ThemeConfig = this.generateTheme(sub);
                this._themingService.setTheme(newTheme, sub.id);
                this.setAppLanguage();

                localStorage.setItem(this.LOCAL_STORAGE_CURRENT_SUB, JSON.stringify(sub));
            });
    }

    //FIXME: mudar aqui, não precisa mais desse método, pode acessar diretamente
    public isFeatureActive(featureType: string): boolean {
        const currentSubscription = this.currentSubscription$.getValue();
        switch (featureType) {
            case "CoursesCatalog":
                return environment.forceCoursesCatalog || currentSubscription.features?.coursesCatalog?.active || false;
            case "CanRegister":
                return currentSubscription?.features?.canRegister?.active || false;
            case "Quiz":
                return currentSubscription?.features?.quiz?.active || false;
            case "Review":
                return currentSubscription?.features?.review?.active || false;
            case "VideoChat":
                return currentSubscription?.features?.videoChat?.active || false;
            case "Checkout":
                return currentSubscription?.features?.checkout?.active || false;
            default:
                throw `${featureType} not implemented`;
        }
    }

    public setCurrentSubscriptionId(subscriptionId: string): void {
        const newSub = this.subscriptions$.getValue().find((x) => x.id == subscriptionId);
        this.currentSubscription$.next(newSub);
    }

    public ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    private generateTheme(sub: SubscriptionDTO): ThemeConfig {
        return {
            themeId: sub.id,
            mainColors: {
                primaryColor: sub.theme.primaryColor,
                accentColor: sub.theme.accentColor,
                warnColor: sub.theme.warnColor
            },
            images: {
                primaryLogoUrl: sub.theme.primaryLogoUrl,
                secondaryLogoUrl: sub.theme.secondaryLogoUrl,
                poweredByLogoUrl: sub.theme.poweredByLogoUrl,
                bannerUrl: sub.theme.bannerUrl,
                favIconUrl: sub.theme.favIconUrl ?? "assets/icons/app-icon.png"
            }
        } as ThemeConfig;
    }

    public setAppLanguage(): void {
        const subDefaultLang = this.currentSubscription$?.value?.settings?.defaultLanguage;
        let defaultLang = "pt-BR";

        if (subDefaultLang) {
            switch (subDefaultLang) {
                case LanguageDTO.enUS:
                    defaultLang = "en-US";
                    break;
                case LanguageDTO.esES:
                    defaultLang = "es-ES";
                    break;
                case LanguageDTO.ptBR:
                    defaultLang = "pt-BR";
                    break;
            }
        } else {
            try {
                const localLang = navigator.language.split("-")[0];
                if (localLang == "en") defaultLang = "en-US";
                else if (localLang == "es") defaultLang = "es-ES";
            } catch {}
        }

        console.log("Navigator Language:", navigator.language);
        console.log("Default Language:", defaultLang);
        this.translateService.use(defaultLang);
        localStorage.setItem("locale", defaultLang);
    }
}
